import { atom } from 'jotai';
import orderBy from 'lodash/orderBy';

import { filesQueryAtom } from '../_graphql/queries/filesQuery';
import {
  FileRecord,
  FileViewMode,
  FileSortOption,
  FolderPathSegment,
} from '../types/files';

import { $directoriesById, $directoryTree } from './fileTree.state';

import { atomFromQueryString } from '~/shared/state';

// 🟣🟣🟣 PARAMS
export const $currentFolderId = atomFromQueryString.string('folderId');
export const $viewMode = atomFromQueryString.string<FileViewMode>('view');
export const $sortBy = atomFromQueryString.string<FileSortOption>('sortBy');
export const $searchTerm = atomFromQueryString.string('search');

// 🔘🔘🔘 PLAIN ATOMS
export const $selectedFileIds = atom<Set<string>>(new Set<string>());
export const $isSelectionMode = atom<boolean>(false);
// const $localChanges = atom<LocalChange<FileRecord>[]>([]);

// 🟢🟢🟢 REMOTE DATA
export const $remoteFiles = filesQueryAtom()((get) => ({
  variables: {
    folderId: get($currentFolderId) || null,
    searchTerm: get($searchTerm) || '',
  },
}));

// 🟢🟢🟢 COMPUTED DATA
export const $files = atom((get) => {
  const remoteFilesResult = get($remoteFiles);
  const currentFolderId = get($currentFolderId);
  const searchTerm = get($searchTerm);
  const sortBy = get($sortBy) || 'name-asc';

  const remoteFiles = remoteFilesResult.data || [];
  if (remoteFiles.length === 0) return [];

  // When searching, show all results without folder filtering
  // When not searching, filter by current folder
  let files: FileRecord[];
  if (searchTerm && searchTerm.trim()) {
    // Search mode: show all results from anywhere in the database
    files = remoteFiles;
  } else {
    // Browse mode: filter by current folder
    files = currentFolderId
      ? remoteFiles.filter((f: FileRecord) => f.parentId === currentFolderId)
      : remoteFiles.filter((f: FileRecord) => !f.parentId);
  }

  // Sort files
  const [field, direction] = sortBy.split('-') as [string, 'asc' | 'desc'];
  files = orderBy(files, [field], [direction]);

  return files;
});

// 🟢🟢🟢 COMPUTED DATA - Directories with parent references
// This atom explicitly encapsulates the dependency on $directoryTree
const $directoriesByIdWithParents = atom((get) => {
  get($directoryTree); // Ensures parent references are set
  return get($directoriesById);
});

// 🟢🟢🟢 COMPUTED DATA - Current folder path
export const $currentFolderPath = atom((get) => {
  const currentFolderId = get($currentFolderId);

  if (!currentFolderId) {
    return []; // Root level - no breadcrumbs
  }

  const { data: directoriesById } = get($directoriesByIdWithParents);

  if (!directoriesById || directoriesById.size === 0) {
    return []; // Data not loaded yet
  }

  // Build path by traversing up the parent references
  const path = buildPathFromTree(currentFolderId, directoriesById);

  return path;
});

/**
 * Builds folder path by traversing up the directory hierarchy using parent references
 * @param folderId - Current folder ID
 * @param directoriesById - Map of directory ID to FileRecord with parent references
 * @returns Array of path segments from root to current folder
 */
function buildPathFromTree(
  folderId: string,
  directoriesById: Map<string, FileRecord>,
): FolderPathSegment[] {
  const path: FolderPathSegment[] = [];
  let currentDir = directoriesById.get(folderId);
  let iterations = 0;
  const MAX_DEPTH = 100; // Prevent infinite loops

  // Traverse up using parent references
  while (currentDir && iterations < MAX_DEPTH) {
    path.unshift({
      id: currentDir.id,
      name: currentDir.name,
    });

    currentDir = currentDir.parent;
    iterations++;
  }

  if (iterations >= MAX_DEPTH && currentDir) {
    console.warn(
      'buildPathFromTree: MAX_DEPTH reached, possible cycle detected',
      { folderId, iterations, lastDirId: currentDir.id },
    );
  }

  return path;
}

// 🟢🟢🟢 COMPUTED DATA - Files by type
export const $filesByType = atom((get) => {
  const files = get($files);

  return {
    folders: files.filter((f: FileRecord) => f.type === 'Directory'),
    files: files.filter((f: FileRecord) => f.type === 'Regular'),
  };
});

// 🟠🟠🟠 ACTION -> Select files
export const $toggleFileSelection = atom(null, (get, set, fileId: string) => {
  const selectedIds = new Set(get($selectedFileIds));
  if (selectedIds.has(fileId)) {
    selectedIds.delete(fileId);
  } else {
    selectedIds.add(fileId);
  }
  set($selectedFileIds, selectedIds);
});

// 🟠🟠🟠 ACTION -> Select single file (replace entire selection)
export const $selectSingleFile = atom(null, (get, set, fileId: string) => {
  set($selectedFileIds, new Set([fileId]));
});

// 🟠🟠🟠 ACTION -> Clear selection
export const $clearSelection = atom(null, (get, set) => {
  set($selectedFileIds, new Set<string>());
});

// 🟠🟠🟠 ACTION -> Select all
export const $selectAllFiles = atom(null, (get, set) => {
  const files = get($files);
  set($selectedFileIds, new Set(files.map((f: FileRecord) => f.id)));
});

// 🟠🟠🟠 ACTION -> Toggle selection mode
export const $toggleSelectionMode = atom(null, (get, set) => {
  const current = get($isSelectionMode);
  set($isSelectionMode, !current);
  if (current) {
    // Exiting selection mode, clear selection
    set($clearSelection);
  }
});

// 🟠🟠🟠 ACTION -> Exit selection mode
export const $exitSelectionMode = atom(null, (get, set) => {
  set($isSelectionMode, false);
  set($clearSelection);
});
